import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, TextField, MenuItem, Typography, Button, Fab, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DataTable from '../components/common/DataTable';
import TableFilters from '../components/common/TableFilters';
import ActionPopover from '../components/common/ActionPopover';
import ConfirmDialog from '../components/common/ConfirmDialog';
import CreateEntityDialog from '../components/common/CreateEntityDialog';
import StatusChip from '../components/common/StatusChip';
import { ORDERS_COLUMNS } from '../config/tableColumnsConfig';
import { ORDERS_FILTERS } from '../config/filterConfig';
import { ORDER_STATUS } from '../config/statusConfig';
import ordersApi from '../services/ordersApi';
import quotationsApi from '../services/quotationsApi';
import { useToast } from '../components/common/ToastProvider';

/**
 * My Orders Page
 * Displays orders table with filters, sorting, and actions
 */
const MyOrders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ column: '', direction: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  
  console.log(loading)
  // Action popover state
  const [actionAnchor, setActionAnchor] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  
  // Quotation details popover state
  const [quotationAnchorEl, setQuotationAnchorEl] = useState(null);
  const [quotationData, setQuotationData] = useState(null);
  const [revisionHistory, setRevisionHistory] = useState([]);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [quotationOptions, setQuotationOptions] = useState([]);
  const { showToast } = useToast();

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ordersApi.list(filters, sort, page, rowsPerPage);
      setOrders(response.data);
      setTotalRows(response.total);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, rowsPerPage]);

  useEffect(() => {
    fetchOrders();
    // Note: React.StrictMode intentionally double-invokes effects in development
    // This helps catch bugs but may cause duplicate API calls in development mode
  }, [fetchOrders]);

  // Fetch quotation options for autocomplete - only once on mount
  useEffect(() => {
    let isMounted = true;
    const fetchQuotationOptions = async () => {
      // Only fetch if options haven't been loaded yet
      if (quotationOptions.length > 0) return;
      
      try {
        const response = await quotationsApi.list({}, {}, 0, 1000);
        if (isMounted) {
          const options = response.data.map(q => ({
            value: q.quotationNumber,
            label: `${q.quotationNumber} - ${q.clientName}`
          }));
          setQuotationOptions(options);
        }
      } catch (error) {
        console.error('Error fetching quotation options:', error);
      }
    };
    fetchQuotationOptions();
    
    return () => {
      isMounted = false;
    };
  }, [quotationOptions.length]); // Empty dependency array - only run once

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page
  };

  // Handle sort change
  const handleSortChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setSort({ column: '', direction: '' });
    } else {
      const [column, direction] = value.split('-');
      setSort({ column, direction });
    }
    setPage(0);
  };

  // Handle action button click
  const handleActionClick = (event, row) => {
    setActionAnchor(event.currentTarget);
    setSelectedOrder(row);
    setUpdateStatus(row.orderStatus);
  };

  // Handle update order
  const handleUpdateOrder = async () => {
    if (selectedOrder && updateStatus) {
      await ordersApi.update(selectedOrder.id, { orderStatus: updateStatus });
      setActionAnchor(null);
      setSelectedOrder(null);
      fetchOrders();
    }
  };

  // Handle quotation number click
  const handleQuotationClick = async (row, columnId, event) => {
    if (columnId === 'quotationNumber') {
      setQuotationAnchorEl(event?.target || null);
      setQuotationData(row);
      // Fetch revision history
      const history = await quotationsApi.getRevisionHistory(row.quotationNumber);
      setRevisionHistory(history);
    }
  };

  // Handle delete
  // const handleDeleteClick = (row) => {
  //   setOrderToDelete(row);
  //   setDeleteDialogOpen(true);
  // };

  const confirmDelete = async () => {
    if (orderToDelete) {
      await ordersApi.delete(orderToDelete.id);
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      fetchOrders();
    }
  };

  // Handle create order
  const handleCreateOrder = async (formData) => {
    try {
      // Convert date to ISO string if it exists
      const orderData = {
        ...formData,
        orderDate: formData.orderDate || new Date().toISOString().split('T')[0]
      };
      const response = await ordersApi.create(orderData);
      if (response.success) {
        showToast(response.message || 'Order created successfully', 'success');
        setCreateDialogOpen(false);
        fetchOrders();
      } else {
        showToast(response.error || 'Failed to create order', 'error');
      }
    } catch (error) {
      showToast('Failed to create order', 'error');
    }
  };

  // Create order form fields
  const createOrderFields = [
    { id: 'clientName', label: 'Client Name', type: 'text', required: true },
    { id: 'location', label: 'Location', type: 'text', required: true },
    { 
      id: 'quotationNumber', 
      label: 'Related Quotation Number', 
      type: 'autocomplete', 
      required: false
    },
    { id: 'orderValue', label: 'Order Value', type: 'number', required: true },
    { 
      id: 'orderStatus', 
      label: 'Order Status', 
      type: 'select', 
      required: true,
      options: ORDER_STATUS.map(status => ({ value: status, label: status })),
      showStatusChip: true
    },
    { id: 'orderDate', label: 'Order Date', type: 'date', required: true }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600 }}>
          My Orders
        </Typography>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center', width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          {/* Sort Dropdown */}
          <TextField
            select
            label="Sort By"
            value={sort.column && sort.direction ? `${sort.column}-${sort.direction}` : ''}
            onChange={handleSortChange}
            size="small"
            sx={{ 
              minWidth: { xs: '100%', sm: 180 }, 
              fontSize: '0.875rem',
              flex: { xs: '1 1 auto', sm: '0 0 auto' }
            }}
          >
          <MenuItem value="">No Sort</MenuItem>
          <MenuItem value="clientName-asc">Client Name (A-Z)</MenuItem>
          <MenuItem value="clientName-desc">Client Name (Z-A)</MenuItem>
          <MenuItem value="orderDate-asc">Order Date (Oldest First)</MenuItem>
          <MenuItem value="orderDate-desc">Order Date (Newest First)</MenuItem>
          <MenuItem value="orderValue-asc">Order Value (Low to High)</MenuItem>
          <MenuItem value="orderValue-desc">Order Value (High to Low)</MenuItem>
          </TextField>
          {/* Add New Button - Hidden on mobile (shown as FAB) */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              backgroundColor: 'secondary.main',
              color: 'secondary.contrastText',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'secondary.dark',
              },
            }}
          >
            Add New Order
          </Button>
        </Box>
      </Box>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="secondary"
          aria-label="add new order"
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Filters */}
      <TableFilters
        filters={ORDERS_FILTERS}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        statusOptions={ORDER_STATUS}
      />

      {/* Orders Table */}
      <DataTable
        columns={ORDERS_COLUMNS}
        data={orders}
        onActionClick={handleActionClick}
        onCellClick={handleQuotationClick}
        page={page}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalRows={totalRows}
        mobileHiddenColumns={['location', 'orderValue']}
      />

      {/* Update Order Popover */}
      <ActionPopover
        open={Boolean(actionAnchor)}
        anchorEl={actionAnchor}
        onClose={() => {
          setActionAnchor(null);
          setSelectedOrder(null);
        }}
        onSubmit={handleUpdateOrder}
        title="Update Order"
      >
        <TextField
          select
          label="Order Status"
          value={updateStatus}
          onChange={(e) => setUpdateStatus(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          {ORDER_STATUS.map((status) => (
            <MenuItem key={status} value={status}>
              <StatusChip status={status} />
            </MenuItem>
          ))}
        </TextField>
      </ActionPopover>

      {/* Quotation Details Popover */}
      <ActionPopover
        open={Boolean(quotationAnchorEl)}
        anchorEl={quotationAnchorEl}
        onClose={() => {
          setQuotationAnchorEl(null);
          setQuotationData(null);
          setRevisionHistory([]);
        }}
        title={`Quotation Details: ${quotationData?.quotationNumber || ''}`}
      >
        {quotationData && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Client:</strong> {quotationData.clientName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Location:</strong> {quotationData.location}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Revision History:
            </Typography>
            {revisionHistory.length > 0 ? (
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', mt: 1 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Revision</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Value</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {revisionHistory.map((rev, idx) => (
                    <tr key={idx} style={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider' }}>{rev.revision}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider' }}>{rev.date}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', fontWeight: 500 }}>
                        ₹{rev.value.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <StatusChip status={rev.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No revision history available
              </Typography>
            )}
          </Box>
        )}
      </ActionPopover>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Order"
        message={`Are you sure you want to delete order ${orderToDelete?.quotationNumber}?`}
      />

      {/* Create Order Dialog */}
      <CreateEntityDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateOrder}
        title="Add New Order"
        fields={createOrderFields}
        relatedEntityOptions={{
          quotationNumber: quotationOptions
        }}
        initialValues={{
          clientName: '',
          location: '',
          quotationNumber: '',
          orderValue: '',
          orderStatus: '',
          orderDate: null
        }}
      />
    </Container>
  );
};

export default MyOrders;

